import type { DocType } from '@eriko/shared-types';

/**
 * Filename signals that a document is a tax form / tax return (any jurisdiction)
 * rather than a receipt, invoice, or statement. Covers the US + UK forms Eriko
 * scans: Schedule C / Form 1040 / W-2 / W-9 / 1099, SA100/SA103 self-assessment,
 * VAT return, CT600, CIS300, and PAYE forms (P45/P60/P11D).
 *
 * Used both to classify uploads as TAX_DOC and to keep tax forms out of the
 * "attach a receipt" picker — a Schedule C is never a receipt for a bank line.
 */
const TAX_FORM_PATTERN =
  /(\b1040\b|\b1099\b|\bw-?[29]\b|\bsa1\d\d[a-z]?\b|\bct600\b|cis[\s-]?300|\bcis\b|\bvat\b|\bp11d\b|\bp45\b|\bp60\b|schedule[\s-]?[a-f]\b|self.?assess|self.?employ|\btax(?!i))/;

export function looksLikeTaxForm(fileName: string): boolean {
  return TAX_FORM_PATTERN.test(fileName.toLowerCase());
}

/**
 * Best-effort client-side guess from filename + mime.
 * Used at upload time; the user can override in the dialog before confirming.
 */
export function guessDocType(fileName: string, fileType: string): DocType {
  const name = fileName.toLowerCase();
  if (name.endsWith('.csv')) return 'BANK_STATEMENT';

  const isPdf = fileType === 'application/pdf' || name.endsWith('.pdf');
  const isImage = fileType.startsWith('image/');

  if (isPdf) {
    if (/(statement|bank)/.test(name)) return 'BANK_STATEMENT';
    if (looksLikeTaxForm(name)) return 'TAX_DOC';
    if (/bill/.test(name)) return 'BILL';
    if (/invoice/.test(name)) return 'INVOICE';
    if (/receipt/.test(name)) return 'RECEIPT';
    return 'OTHER';
  }

  if (isImage) {
    // A photographed tax form is still a tax form, not a receipt.
    if (looksLikeTaxForm(name)) return 'TAX_DOC';
    return 'RECEIPT';
  }
  return 'OTHER';
}
